import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import format from "date-fns/format";
import { FilterComponent } from "@client/shared/components";
import { globalStyles, PAGE_KEYS } from "@client/shared/constants";
import {
  exportToFile,
  queryStringBuilder,
  getPageConfig,
  setPageConfig,
} from "@client/utils";
import WhitelistView from "./view";
import Service from "../service";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import style from "./style";

let gridHelper = null;

const defaultState = {
  entries: [],
  entriesCount: 0,
  isLoading: false,
  isExporting: false,
  pageNumber: 1,
  pageSize: 5,
  order: null,
  orderBy: "",
  filters: [],
  selectedRows: [],
};

const Whitelist = () => {
  const pageConfig = getPageConfig(PAGE_KEYS.ADMIN_WHITELIST);

  const [state, setState] = useState({
    ...defaultState,
    pageSize: pageConfig?.pageSize || defaultState.pageSize,
  });

  const fetchEntries = useCallback(
    async (
      pageNumber = 1,
      pageSize = 5,
      order = "",
      orderBy = "",
      filters = []
    ) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      const queryString = queryStringBuilder(
        pageSize,
        (pageNumber - 1) * pageSize,
        [],
        filters,
        { field: orderBy, order }
      );
      const { data, error } = await Service.getWhiteList(queryString);
      if (error) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        return toast.error(
          Array.isArray(error) ? error[0].message : error.message
        );
      }
      setState((prevState) => ({
        ...prevState,
        entries: data?.whitelistedClients || defaultState.entries,
        entriesCount: data?.count || defaultState.entriesCount,
        isLoading: false,
      }));
    },
    []
  );

  const handlePageNumberChange = useCallback((pageNumber) => {
    setState((prevState) => ({
      ...prevState,
      pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPageConfig(PAGE_KEYS.ADMIN_WHITELIST, {
      pageSize,
    });
    setState((prevState) => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleSortChange = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
    }));
  }, []);

  const handleSelectionChange = useCallback((selectedRows) => {
    setState((prevState) => ({
      ...prevState,
      selectedRows,
    }));
  }, []);

  const handleExport = useCallback(
    async (sortObj = null, filters = [], selectedRows = []) => {
      setState((prevState) => ({
        ...prevState,
        isExporting: true,
      }));
      let queryString = queryStringBuilder(0, 0, [], filters, sortObj);
      if (!!selectedRows.length) {
        queryString += `${queryString ? "&" : "?"}ids=${selectedRows.join(
          ","
        )}`;
      }
      const { data, error } = await Service.exportFile("Clients", queryString);
      if (error) {
        toast.error(
          Array.isArray(error) ? error[0].message : error.message || error
        );
      } else {
        exportToFile(data, "admin_whitelist_export");
      }
      setState((prevState) => ({
        ...prevState,
        isExporting: false,
      }));
    },
    []
  );

  const filterConfig = [
    {
      field: "name",
      fieldToDisplay: "Client Name",
      operatorType: "string",
    },
    {
      field: "rcAccountId",
      fieldToDisplay: "Rc Acct UID",
      operatorType: "string",
    },
    {
      field: "type",
      fieldToDisplay: "Role",
      operatorType: "number",
      options: [
        { label: "Customer", value: "Customer" },
        { label: "Partner", value: "Partner" },
      ],
    },
    {
      field: "activationDate",
      fieldToDisplay: "Activation Date",
      operatorType: "number",
      isDateField: true,
    },
    {
      field: "status",
      fieldToDisplay: "Status",
      operatorType: "number",
      options: [
        { label: "Active", value: "Active" },
        { label: "Pending", value: "Pending" },
        { label: "Expired", value: "Expired" },
        { label: "Revoked", value: "Revoked" },
      ],
    },
    {
      field: "expiration_date",
      fieldToDisplay: "Expiration Date",
      operatorType: "number",
      isDateField: true,
    },
  ];

  const columnConfig = [
    {
      id: "name",
      label: "CLIENT NAME",
      field: "name",
      canSort: true,
      hasEllipses: true,
      noResize: true,
    },
    {
      id: "rcAccountId",
      field: "rcAccountId",
      label: "RC ACCOUNT UID",
      hasEllipses: true,
      canSort: true,
      noResize: true,
    },
    {
      id: "type",
      field: "type",
      label: "CLIENT TYPE",
      hasEllipses: true,
      canSort: true,
      noResize: true,
    },
    {
      id: "activationDate",
      field: "activationDate",
      label: "ACTIVATION DATE",
      canSort: true,
      noResize: true,
      render: (row) => {
        const activationDate = row.activationDate
          ? format(new Date(row.activationDate), "MMM dd, yyyy") || ""
          : "";
        return (
          <Tooltip placement="top-start" title={activationDate}>
            <Typography variant="body2" noWrap>
              {activationDate}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "expirationDate",
      field: "expirationDate",
      label: "EXPIRATION DATE",
      noResize: true,
      canSort: true,
      render: (row) => {
        const renderText = row.expirationDate
          ? format(new Date(row.expirationDate), "MMM dd, yyyy")
          : " -";
        return (
          <Tooltip title={renderText} placement="top-start">
            <Typography variant="body2" noWrap>
              {renderText}
            </Typography>
          </Tooltip>
        );
      },
    },

    {
      id: "status",
      field: "status",
      label: "STATUS",
      hasEllipses: true,
      canSort: true,
      noResize: true,
    },
    {
      id: "logo",
      field: "logo",
      label: "LOGO",
      noResize: true,
      render: (row) => {
        if (row.clientLogoPath) {
          return (
            <img width="124" height="36" alt="logo" src={row.clientLogoPath} />
          );
        } else {
          return (
            <Box
              sx={{
                ...globalStyles.display.flex,
                ...globalStyles.flex.justify.center,
                ...globalStyles.flex.align.center,
                ...style.emptyLogoDiv,
              }}
            >
              <Typography className="align-center mt-2" variant="body2">
                Not Available
              </Typography>
            </Box>
          );
        }
      },
    },
    {
      id: "col-action",
      label: "",
      renderHeader: () => (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.end,
          }}
        >
          {" "}
          <Tooltip title="Export" placement="top-start">
            <IconButton
              disabled={state.isExporting}
              sx={{ mr: 0 }}
              onClick={() => {
                if (!state.entries.length) {
                  return toast.error("No data to export");
                }
                handleExport(
                  { field: state.orderBy, order: state.order },
                  state.filters,
                  state.selectedRows
                );
              }}
            >
              <img src="/assets/export.svg" alt="Export" />
            </IconButton>
          </Tooltip>
          <FilterComponent
            filterConfig={filterConfig}
            onFilterApply={handleFilterChange}
            appliedFilters={state.filters}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchEntries(
      state.pageNumber,
      state.pageSize,
      state.order,
      state.orderBy,
      state.filters
    );
  }, [
    fetchEntries,
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
  ]);

  return (
    <WhitelistView
      entries={state.entries}
      entriesCount={state.entriesCount}
      columnConfig={columnConfig}
      isLoading={state.isLoading}
      pageNumber={state.pageNumber}
      pageSize={state.pageSize}
      order={state.order}
      orderBy={state.orderBy}
      handlePageNumberChange={handlePageNumberChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChange={handleSortChange}
      handleSelectionChange={handleSelectionChange}
      handleGridReady={(instance) => (gridHelper = instance)}
    />
  );
};

export default Whitelist;
